import { createFileRoute, Link } from "@tanstack/react-router";
import { Phone, ArrowRight, Snowflake, ShieldCheck, Gauge, Wrench, CheckCircle, AlertTriangle, PoundSterling } from "lucide-react";
import { getBusinessConfig } from "@/lib/site-config";
import { seoHead, serviceSchema, localBusinessSchema, complianceToBusinessOpts } from "@/lib/seo";
import { faqSchema, FAQAccordion } from "@/components/FAQAccordion";
import { ReassuranceBanner } from "@/components/ReassuranceBanner";
import { CTABlock } from "@/components/CTABlock";
import { AnimatedSection, StaggerContainer, StaggerItem } from "@/components/AnimatedSection";
import { PrimaryCTA } from "@/components/PrimaryCTA";
import { getPublicConfig } from "@/lib/site-content.functions";
import { ServiceTestimonials } from "@/components/ServiceTestimonials";
import { InternalLinksBlock } from "@/components/InternalLinksBlock";

const faqs = [
  {
    q: "How much does an air con regas cost in London?",
    a: "An air con regas at an independent garage like ours is significantly cheaper than a main dealer. The exact price depends on which refrigerant your vehicle uses — R1234yf (the newer gas fitted to most cars from ~2017 onwards) costs more than the older R134a because the gas itself is more expensive. We'll confirm your gas type and give you a written price before any work starts. No upselling.",
  },
  {
    q: "What's the difference between R134a and R1234yf gas?",
    a: "R134a is the older refrigerant used on most cars built before ~2017. R1234yf is the newer, lower-global-warming-potential gas mandated on most newer cars. They are NOT interchangeable — putting the wrong gas in a system can damage the compressor and void warranties. Call us with your reg before you book and we'll confirm we can support your vehicle's refrigerant.",
  },
  {
    q: "How often should I regas my car's air conditioning?",
    a: "Most manufacturers recommend a regas every 2 years. Air con systems lose a small amount of refrigerant naturally over time even when sealed — once the charge drops below about 70% the system can't cool effectively and the compressor can run hot. If your air con has never been regassed and the car is more than two years old, it's almost certainly due.",
  },
  {
    q: "Why is my air con blowing warm air?",
    a: "The most common cause is low refrigerant — either from normal seepage over years, or from a leak in a pipe, condenser or seal. Other causes include a failed compressor clutch, a blocked condenser, a stuck blend door, or a failed pressure sensor. A regas with leak-dye is the cheapest way to diagnose: if the system holds pressure afterwards, it was just low; if it loses pressure within days, we trace the dye to the leak.",
  },
  {
    q: "Why does my air con smell musty?",
    a: "Bacteria and mould grow on the damp evaporator behind the dashboard, especially if the system isn't used regularly in winter. We offer an antibacterial anti-fungal treatment that's sprayed into the system to kill the biofilm and leave the cabin smelling fresh. Often combined with a new pollen filter for the best result.",
  },
  {
    q: "Do you offer a free leak check?",
    a: "Yes. If your air con isn't cooling we'll do a free initial pressure and visual check to see whether you need a simple regas or a leak repair. We'll quote in writing before any chargeable work starts.",
  },
  {
    q: "Can you regas air con on hybrids and EVs?",
    a: "Yes — hybrids and most EVs use R1234yf and are regassed in the same way as a petrol car. EV air con also cools the high-voltage battery on many models, so keeping the charge correct can actually improve range and rapid-charging performance in hot weather.",
  },
  {
    q: "How long does an air con regas take?",
    a: "About 45–60 minutes for a standard regas, including evacuation, vacuum hold-test, and recharge. We'll usually book it as a wait-while appointment. A regas with leak diagnosis can take longer if we trace the dye on a return visit.",
  },
];

export const Route = createFileRoute("/services/air-con-regas")({
  loader: async () => {
    const cfg = await getPublicConfig().catch(() => null);
    return { compliance: cfg?.compliance || null };
  },
  head: ({ loaderData }) =>
    seoHead({
      path: "/services/air-con-regas",
      title: "Air Con Regas & Service East London E14 | Woodseer Autos",
      description:
        "Car air conditioning regas in Poplar, E14. Antibacterial treatment, free initial check, fixed independent prices — cheaper than main dealers. Call with your reg to confirm refrigerant.",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Services", path: "/services" },
        { name: "Air Con Regas", path: "/services/air-con-regas" },
      ],
      jsonLd: [
        localBusinessSchema(complianceToBusinessOpts(loaderData?.compliance)),
        faqSchema(faqs),
        serviceSchema({
          name: "Air Conditioning Regas & Service — Woodseer Autos",
          serviceType: "Automotive Air Conditioning Service",
          description:
            "Independent car air conditioning regas, leak check and antibacterial treatment in East London. Call with your registration to confirm refrigerant compatibility.",
        }),
      ],
    }),
  component: AirConRegasPage,
});

const INCLUDED = [
  "Identification of correct refrigerant type from under-bonnet label (subject to supported gases — confirm before booking)",
  "Full system evacuation and vacuum hold-test to detect major leaks",
  "Recharge to manufacturer specification with electronic-scale accuracy",
  "PAG compressor oil top-up where required",
  "UV leak-detection dye added on request",
  "Performance test — vent temperature measured before and after",
  "Pollen / cabin filter inspection",
  "Optional antibacterial anti-fungal treatment to kill musty smells",
  "Written report and invoice with refrigerant type and charge weight",
];

const SIGNS = [
  "Air con blows warm, lukewarm or only cools weakly on max",
  "Musty, damp or sweaty-sock smell when the air con is switched on",
  "Hissing or gurgling noises from the dashboard with air con on",
  "Visible fogging on the windscreen that won't clear (failed dehumidification)",
  "Air con clutch won't engage or cycles on and off rapidly",
  "It's been more than 2 years since the last regas",
  "Reduced electric range on a hybrid or EV in hot weather",
];

function AirConRegasPage() {
  const biz = getBusinessConfig();

  return (
    <>
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-b from-secondary/40 to-background border-b border-border">
        <div className="mx-auto max-w-5xl px-4 py-14 md:py-20">
          <p className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/5 px-3 py-1 text-xs font-bold text-primary uppercase tracking-[0.18em]">
            <Snowflake className="h-3.5 w-3.5" /> Air Conditioning
          </p>
          <h1 className="mt-5 display-lg text-foreground">
            Air Con Regas &amp; Service in East London
          </h1>
          <p className="mt-5 text-base md:text-lg text-muted-foreground max-w-2xl leading-relaxed">
            Independent car air conditioning regas at our Poplar workshop. Free initial leak
            check, antibacterial treatment available, fixed independent-garage price — a lot
            kinder than a main dealer. Refrigerant support is subject to your vehicle's gas type
            (most cars use R134a or R1234yf) — give us a call with your reg and we'll confirm
            we can do it before you book.
          </p>
          <PrimaryCTA location="air-con-hero" intent="air con quote" variant="light" />
          <div className="mt-4">
            <a
              href="#faqs"
              className="inline-flex items-center gap-2 rounded-md px-3 py-2.5 text-sm font-semibold text-primary hover:underline underline-offset-4"
            >
              Jump to air con FAQs <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>

      <ReassuranceBanner />

      {/* Value vs dealer */}
      <AnimatedSection>
        <section className="py-12 md:py-16">
          <div className="mx-auto max-w-5xl px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground tracking-tight">
              Main-dealer cooling, independent-garage price
            </h2>
            <p className="mt-3 text-muted-foreground max-w-2xl">
              A main-dealer air con regas in London commonly costs more than the same job at an
              independent. We use calibrated regas machines and OE-grade PAG oils — but without
              the dealer overheads. You get the right gas for your vehicle, weighed in to the
              manufacturer spec, and a written invoice you can keep with the service record.
              Refrigerant support depends on your vehicle — call with your reg before booking and
              we'll confirm.
            </p>
            <StaggerContainer className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { icon: PoundSterling, title: "Fixed independent price", desc: "Written quote up front. No nasty surprises, no upselling, no scare stories about your compressor." },
                { icon: Gauge, title: "Right gas for your car", desc: "We identify your vehicle's refrigerant from the under-bonnet label and confirm compatibility before any work starts." },
                { icon: Wrench, title: "Leak check included", desc: "Free initial pressure and visual check. UV dye on request so we can trace any persistent leak on a return visit." },
              ].map((c) => (
                <StaggerItem key={c.title}>
                  <div className="surface-premium rounded-xl p-5 h-full">
                    <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <c.icon className="h-5 w-5" />
                    </div>
                    <h3 className="mt-4 text-base font-bold text-foreground">{c.title}</h3>
                    <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{c.desc}</p>
                  </div>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </section>
      </AnimatedSection>

      {/* What's included */}
      <section className="py-12 md:py-16 bg-secondary/30 border-y border-border">
        <div className="mx-auto max-w-5xl px-4 grid grid-cols-1 md:grid-cols-2 gap-10">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground tracking-tight">
              What's included in an air con regas
            </h2>
            <p className="mt-3 text-muted-foreground">
              An air con regas is more than just "topping it up". We evacuate the old refrigerant,
              vacuum-test the system for leaks, then weigh in fresh gas and PAG oil to the
              manufacturer's exact spec. Here's the full process.
            </p>
          </div>
          <ul className="space-y-2.5">
            {INCLUDED.map((item) => (
              <li key={item} className="flex items-start gap-2.5 text-sm text-foreground">
                <CheckCircle className="h-4 w-4 text-primary shrink-0 mt-0.5" aria-hidden="true" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Signs */}
      <AnimatedSection>
        <section className="py-12 md:py-16">
          <div className="mx-auto max-w-5xl px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground tracking-tight">
              Signs your air con needs a regas
            </h2>
            <ul className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3">
              {SIGNS.map((s) => (
                <li
                  key={s}
                  className="flex items-start gap-2.5 rounded-lg border border-border bg-card p-4 text-sm text-foreground"
                >
                  <AlertTriangle className="h-4 w-4 text-primary shrink-0 mt-0.5" aria-hidden="true" />
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      </AnimatedSection>

      {/* Honest about leaks */}
      <section className="py-12 md:py-16 bg-secondary/30 border-y border-border">
        <div className="mx-auto max-w-3xl px-4">
          <div className="flex items-start gap-3">
            <ShieldCheck className="h-6 w-6 text-primary shrink-0 mt-1" aria-hidden="true" />
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground tracking-tight">
                Honest about leaks
              </h2>
              <p className="mt-3 text-muted-foreground leading-relaxed">
                If a regas doesn't hold pressure for more than a few days, there's a leak somewhere
                — usually a tired O-ring, a corroded condenser at the front of the car, or a pinhole
                in a pipe. We'll tell you up front, trace the leak with UV dye, and quote the repair
                in writing. We won't keep regassing a leaky system and charging you for the gas.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section id="faqs" aria-labelledby="air-con-faqs-heading" className="py-12 md:py-16 scroll-mt-24">
        <div className="mx-auto max-w-3xl px-4">
          <h2 id="air-con-faqs-heading" className="sr-only">Air con regas FAQs</h2>
          <FAQAccordion faqs={faqs} title="Air con regas — FAQs" />

          <div className="mt-8 rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/8 via-card to-card p-6 md:p-7">
            <h3 className="text-lg md:text-xl font-bold text-foreground tracking-tight">
              Air con not cooling? Let's take a look.
            </h3>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
              Free initial pressure and visual check. Written quote before any chargeable work
              starts. Call with your reg and we'll confirm we can support your vehicle's
              refrigerant.
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <a
                href={biz.phoneHref}
                className="inline-flex items-center gap-2 rounded-md bg-primary px-5 py-2.5 text-sm font-bold text-primary-foreground hover:bg-primary/90 transition-colors"
                aria-label={`Call Woodseer Autos on ${biz.phone}`}
              >
                <Phone className="h-4 w-4" aria-hidden="true" /> Call {biz.phone}
              </a>
              <Link
                to="/book"
                className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-5 py-2.5 text-sm font-bold text-foreground hover:bg-secondary transition-colors"
              >
                Book air con regas <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 rounded-md px-3 py-2.5 text-sm font-semibold text-primary hover:underline underline-offset-4"
              >
                Ask a question <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <ServiceTestimonials title="What East London drivers say" />

      <InternalLinksBlock showHubLink />

      <CTABlock />
    </>
  );
}
